import type { ReadonlyURLSearchParams } from 'next/navigation';
import dayjs from 'dayjs';
import dayJS from '@/lib/dayjs';
import { IListResponse } from '@/shared';
import {
  JobListFilterDto,
  PublicJobListDTO,
} from '@/shared/job/job.public.dto';

const RANKED_TYPES = new Set(['cv', 'view', 'share', 'featured']);
const RANK_NUMBER_TYPES = new Set(['cv', 'view', 'share']);

export function unwrapJobList(
  payload: IListResponse<PublicJobListDTO> | PublicJobListDTO[] | undefined
): { jobs: PublicJobListDTO[]; totalPages: number } {
  if (Array.isArray(payload)) {
    return { jobs: payload, totalPages: 1 };
  }
  if (Array.isArray(payload?.data)) {
    return { jobs: payload.data, totalPages: payload.totalPages ?? 0 };
  }
  const nested = payload?.data as IListResponse<PublicJobListDTO> | undefined;
  if (Array.isArray(nested?.data)) {
    return { jobs: nested.data, totalPages: nested.totalPages ?? 0 };
  }
  return { jobs: [], totalPages: 0 };
}

export function isRankedJobType(type: string | null): boolean {
  return type != null && RANKED_TYPES.has(type);
}

export function shouldShowRankNumber(type: string | null): boolean {
  return type != null && RANK_NUMBER_TYPES.has(type);
}

export function buildJobSearchParams(
  searchParams: ReadonlyURLSearchParams,
  options: { currentPage: number; isNearby: boolean }
): JobListFilterDto {
  const params: Record<string, string | number | boolean> = {};

  searchParams.forEach((value, key) => {
    if (key === 'q') {
      params.search = value;
    } else if (key === 'page') {
      params.page = Number(value) || 1;
    } else if (key === 'lat' || key === 'lng') {
      params[key] = Number(value);
    } else if (key === 'distance') {
      params.distance = value;
    } else {
      params[key] = value;
    }
  });

  if (!params.page) {
    params.page = options.currentPage;
  }

  if (options.isNearby) {
    params.type = 'near-me';
    if (!params.distance) {
      params.distance = '1000';
    }
  }

  return params as JobListFilterDto;
}

export function getTimeTypeId(job: PublicJobListDTO): number | undefined {
  const timeType = job.timetype as number | { id?: number } | undefined;
  if (timeType == null) return undefined;
  return typeof timeType === 'object' ? timeType.id : timeType;
}

export function getLocationName(job: PublicJobListDTO): string {
  const location = job.location as string | { name?: string } | undefined;
  if (location == null) return '';
  return typeof location === 'object' ? (location.name ?? '') : location;
}

export function formatJobSortTime(sortTime?: number | string): string {
  const time = dayJS.unix(Number(sortTime));
  if (time.diff(dayjs(), 'hours') > -24) {
    return time.fromNow();
  }
  return time.format('YYYY-MM-DD HH:mm');
}
